import React, { useEffect, useState } from 'react';

import { LinearProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Trans } from 'react-i18next';
import { isEmpty } from 'lodash';
import { ValidatorForm } from 'react-material-ui-form-validator';

import { LINKS } from 'components/constants';
import { ButtonComponent } from 'components/ButtonComponent/ButtonComponent';
import { AutocompleteValidatorComponent, TextValidatorComponent } from 'components/FormComponents';
import { VALIDATORS_MAP } from 'components/FormComponents/const';
import { TabsComponent } from 'components/TabsComponent/TabsComponent';
import { StatusComponent } from 'pages/planograms/StatusComponent/StatusComponent';
import { PlanogramCloneParams, PlanogramCloneType, PlanogramCloneTypes, PlanogramGroup } from 'types';
import { CloningRequestStatus } from 'types/enums';
import { getTWithPathKey } from 'utils';
import config from 'config/Config';

import { InfoOutlinedIcon } from '../../InfoTooltip/style';

import {
  Box,
  Dialog,
  DialogActions,
  DialogCloseButton,
  DialogContent,
  DialogTitle,
  ErrorOutlineIcon,
  SuccessOutlineIcon,
  Typography
} from '../style';

interface Props {
  open: boolean;
  planogram: PlanogramCloneType;
  planogramGroup: PlanogramGroup[];
  isLoading: boolean;
  handleSubmit: (plGroup: PlanogramCloneParams) => Promise<void>;
  handleClose: () => void;
  handleToggleStatusDialog: () => void;
}

interface ClonePlanogramState {
  name: string | undefined;
  sphereGroup: number | undefined;
  sphereLang: number | undefined;
  spaceName?: string | undefined;
  email: string | undefined;
}

const initialState: ClonePlanogramState = {
  name: undefined,
  sphereGroup: undefined,
  sphereLang: undefined,
  email: undefined,
}

const getButtonType = (cloneType: number) => Object.values(PlanogramCloneTypes)[cloneType];

export function ClonePlanogramDialog({
  open,
  planogram,
  planogramGroup,
  handleSubmit,
  handleClose,
  handleToggleStatusDialog,
  isLoading,
}: Props) {
  const getT = getTWithPathKey('pages.planograms.managePlanograms.cloneDialog');
  const getCommonT = getTWithPathKey('common.buttons');
  const [state, setState] = useState<ClonePlanogramState>(initialState);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<CloningRequestStatus>();
  const [cloneType, setCloneType] = useState<number>(0);
  const tabs = [getT("outside"), getT("inside"), getT("anotherSpace")];

  useEffect(() => {
    getButtonType(cloneType);
    handleStateChange({name: '', spaceName: '', email: '', sphereGroup: ''});
  }, [cloneType, isLoading])

  const handleStateChange = (item: object) =>
    setState(prevState => ({
      ...prevState,
      ...item,
    }));

  const planogramLanguages = (id: number | undefined) => {
    const selectedGroup = planogramGroup
      .find(planogram => id === planogram.id);

    return selectedGroup ?
      selectedGroup.planograms.flatMap(pl => {
        if (pl.id === planogram.id) {
          return [];
        }
        return {
          id: pl.language?.id,
          planogramId: pl.id,
          code: pl.language?.code,
          urlPath: `${pl.language?.code}/${selectedGroup.name}`
        }
      }) : [];
  };

  const renderSyncHint = () => (
    getButtonType(cloneType) === PlanogramCloneTypes.AnotherSpace && (
      <Box mt={2} style={{ display: 'flex' }}>
        <InfoOutlinedIcon className="alignMiddle regular warning" />
        <Typography variant="caption" className="warning">
          <Trans
            i18nKey="pages.planograms.managePlanograms.cloneDialog.assetsSyncHint"
            components={{ a: <a href={LINKS.sphereKnowledgeBase} target="_blank" rel="noreferrer" /> }}
            values={{ spaceName: '' }}
          />
        </Typography>
      </Box>
    )
  )

  const renderContent = () => (
    !isLoading ? (
      <>
        <Box mb={4}>
          <Typography className="plClone">{getT("radioTitle")}</Typography>
          <TabsComponent
            value={cloneType}
            setValue={setCloneType}
            tabNames={tabs}
          />
        </Box>
        <Box mb={4}>
          <Typography className="hintDescription">{getT(`hintDescription.${getButtonType(cloneType)}`)}</Typography>
          {renderSyncHint()}
        </Box>
        {
          getButtonType(cloneType) === PlanogramCloneTypes.Outside && (
            <Box mb={4}>
              <Typography className="plClone">{getT("newGroupTitle")}</Typography>
              <TextValidatorComponent
                validators={[VALIDATORS_MAP.notEmpty('a group name to continue'), VALIDATORS_MAP.lowercaseLettersNumbersUnderscoreDashes]}
                label="Group name"
                value={state.name}
                fieldName="name"
                handleChange={handleStateChange}
                disableMinHeight
              />
            </Box>
          )
        }
        {
          getButtonType(cloneType) === PlanogramCloneTypes.Inside && (
            <Box mb={4}>
              <Box>
                <AutocompleteValidatorComponent
                  value={state.sphereGroup}
                  fieldName="sphereGroup"
                  handleChange={handleStateChange}
                  disableClearable
                  selectList={planogramGroup}
                  itemsNamePath="name"
                  label={getT('groupSelectTitle')}
                  validators={[VALIDATORS_MAP.required]}
                  disableMinHeight
                />
              </Box>
              <Box>
                <AutocompleteValidatorComponent
                  value={state.sphereLang}
                  fieldName="sphereLang"
                  handleChange={handleStateChange}
                  disabled={!state.sphereGroup}
                  disableClearable
                  selectList={planogramLanguages(state.sphereGroup)}
                  itemsNamePath="urlPath"
                  label={getT('languageSelectTitle')}
                  validators={[VALIDATORS_MAP.required]}
                  disableMinHeight
                />
              </Box>
            </Box>
          )
        }
        {
          getButtonType(cloneType) === PlanogramCloneTypes.AnotherSpace && (
            <Box mb={4}>
              <Box>
                <TextValidatorComponent
                  validators={[VALIDATORS_MAP.notEmpty('a group name to continue'), VALIDATORS_MAP.required, VALIDATORS_MAP.spaceName]}
                  label="Space name"
                  value={state.spaceName}
                  fieldName="spaceName"
                  handleChange={handleStateChange}
                  disableMinHeight
                />
              </Box>
              <Box>
                <TextValidatorComponent
                  validators={[VALIDATORS_MAP.isEmail]}
                  label="Send approval request to"
                  value={state.email}
                  fieldName="email"
                  handleChange={handleStateChange}
                  disableMinHeight
                />
                <Typography color="textSecondary" variant="caption">
                  <InfoOutlinedIcon className="regular alignMiddle" />
                  {getT('emailHintDescription')}
                </Typography>
              </Box>
            </Box>
          )
        }
        <DialogActions>
          {!isLoading ? (
            <>
              <ButtonComponent onClick={handleClose} text={getCommonT('cancel')} />
              {getButtonType(cloneType) === PlanogramCloneTypes.AnotherSpace ? (
                <ButtonComponent
                  disabled={isLoading || isEmpty(state.spaceName)}
                  type="submit"
                  color="primary"
                  text={getT('clone')}
                />
              ) : (
                <ButtonComponent
                  disabled={isLoading}
                  type="submit"
                  color="primary"
                  text={getT('clone')}
                />
              )}
            </>
          ) : (
            <ButtonComponent
              onClick={handleClose}
              color="primary"
              text={getT('close')}
            />
          )}
        </DialogActions>
      </>
    ) : (
      <Box mb={4}>
        {getButtonType(cloneType) !== PlanogramCloneTypes.AnotherSpace ? (
          <Typography className="plClone">{getT("copyTitle")}</Typography>
        ) : (
          <Box className="mb-3">
            <Typography className="hintDescription">
              <Trans
                i18nKey="pages.planograms.managePlanograms.cloneDialog.copyDescriptionAnotherSpace"
                components={{ bold: <b /> }}
                values={{ spaceName: 'testSpace' }}
              />
            </Typography>
          </Box>
        )}
        <Box mb={4}>
          <LinearProgress />
        </Box>
        <Box mb={4}>
          <Typography className="hintDescription">{getT(`copyDescription`)}</Typography>
        </Box>
        {renderSyncHint()}
      </Box>
    )
  );

  const getButtonsVariant = () => {
    if (approvalStatus === CloningRequestStatus.APPROVED) {
      return (
        <>
          <ButtonComponent onClick={handleClose} text={getCommonT('cancel')} />
          <ButtonComponent
            type="submit"
            color="primary"
            text={getT('clone')}
          />
        </>
      );
    }

    if (approvalStatus === CloningRequestStatus.DENIED) {
      return (
        <>
          <ButtonComponent onClick={handleClose} text={getCommonT('cancel')} />
          <ButtonComponent
            onClick={handleApprovalRequest}
            color="primary"
            text={getT('resendRequest')}
          />
        </>
      )
    }

    return (
      <>
        <ButtonComponent
          variant="text"
          color="primary"
          className="no-padding"
          onClick={handleApprovalRequest}
          text={getT('resendRequest')}
          style={{ marginRight: 'auto' }}
        />
        <ButtonComponent onClick={handleClose} text={getCommonT('cancel')} />
        <ButtonComponent
          disabled={isLoading || approvalStatus === CloningRequestStatus.PENDING}
          type="submit"
          color="primary"
          text={getT('clone')}
        />
      </>
    )
  };

  const renderConfirmation = () => (
    <>
      <Box style={{ display: 'flex' }}>
        {approvalStatus === CloningRequestStatus.APPROVED && <SuccessOutlineIcon />}
        {approvalStatus === CloningRequestStatus.DENIED && <ErrorOutlineIcon />}
        <Typography className={`hintDescription ${approvalStatus}`}>
          <Trans
            i18nKey={`pages.planograms.managePlanograms.cloneDialog.approvalStatus.${approvalStatus}.message`}
            components={{ bold: <b /> }}
            values={{ email: 'test@gmail.com' }}
          />
        </Typography>
      </Box>
      <DialogActions>{getButtonsVariant()}</DialogActions>
    </>
  );

  const handleApprovalRequest = () => {};

  const handleApprovalStep = () => {
    handleApprovalRequest();
    setApprovalStatus(CloningRequestStatus.PENDING);
    setShowApprovalDialog(true);
  };

  const handleCloseDialog = () => {
    if (approvalStatus) {
      handleToggleStatusDialog();
    }
    handleClose();
  }

  const onSubmit = () => {
    const type = getButtonType(cloneType);
    const languages = planogramLanguages(planogramGroup?.[0].id)?.map(lang => lang.id) || [];
    const planogramDestinationId = planogramLanguages(state.sphereGroup)
      .find(pl => pl.id === state.sphereLang);
    const addedLanguageIds = (type === PlanogramCloneTypes.Outside || type === PlanogramCloneTypes.AnotherSpace) ?
      [...languages] :
      [state.sphereLang];
    const sourceGroup = planogramGroup.find(group =>
      group.planograms.find(pl => pl.id === planogram.id)
    );

    handleSubmit({
      planogramId: planogram.id,
      planogramVersionId: planogram.planogramVersionId,
      languageIds: addedLanguageIds as number[],
      planogramGroupId: state.sphereGroup,
      planogramGroupName: state.name || planogram.name,
      planogramDestinationId: planogramDestinationId?.planogramId,
      sourceGroupId: sourceGroup?.id,
      clientName: state.spaceName,
      email: state.email,
    });
  };

  return open ? (
    <>
      <Dialog open={open} onClose={handleCloseDialog} fullWidth>
        <div className="titleWrapper">
          <DialogTitle id="simple-dialog-title">
            {getT("title", { planogramName: planogram.name })}
          </DialogTitle>
          <StatusComponent statusText={planogram.version} status={planogram.version} useIcon={false} />
        </div>
        <DialogCloseButton onClick={handleCloseDialog}>
          <CloseIcon color="action" />
        </DialogCloseButton>
        <ValidatorForm onSubmit={onSubmit}>
          <DialogContent className="cloneDialog">
            {showApprovalDialog ? renderConfirmation() : renderContent()}
          </DialogContent>
        </ValidatorForm>
      </Dialog>
    </>
  ) : null;
}
